import React, { useState } from 'react';
import { Phone, Send, MessageCircle, Wrench, Ruler, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Layout from '../components/layout/Layout';
import { companyInfo } from '../data/mock';

const CustomPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subject = encodeURIComponent(`Заявка на нестандартную теплицу от ${formData.name}`);
    const body = encodeURIComponent(
      `Имя: ${formData.name}\n` +
      `Телефон: ${formData.phone}\n\n` +
      `Описание требований:\n${formData.description}`
    );
    
    window.location.href = `mailto:${companyInfo.email}?subject=${subject}&body=${body}`;
    
    setTimeout(() => {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', phone: '', description: '' });
      }, 3000);
    }, 500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Layout>
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 py-10">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Фермерские теплицы</h1>
          <p className="text-gray-600">Изготовление нестандартных теплиц по вашему техническому заданию</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Main Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Wrench className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Изготовление по техническому заданию</h2>
                <p className="text-gray-600">Нестандартные размеры и конфигурации</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              Мы изготавливаем фермерские теплицы любых размеров и конфигураций по вашему техническому заданию. 
              Если стандартные размеры вам не подходят — мы разработаем индивидуальный проект специально для вашего участка.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl">
                <Ruler className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Любые размеры</h3>
                  <p className="text-sm text-gray-600">Длина, ширина и высота по вашим требованиям</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl">
                <FileText className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Индивидуальный проект</h3>
                  <p className="text-sm text-gray-600">Разработка под ваше ТЗ</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Гарантия качества</h3>
                  <p className="text-sm text-gray-600">Такая же гарантия, как на серийные модели</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Что мы можем изготовить:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Теплицы увеличенной ширины (4 м, 5 м, 6 м и более)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Теплицы нестандартной длины
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Теплицы с увеличенной высотой
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Блочные теплицы для фермерских хозяйств
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Теплицы с дополнительными дверями и форточками
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Оставить заявку</h2>
            <p className="text-gray-600 mb-6">Опишите ваши требования, и мы свяжемся для расчёта стоимости</p>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Заявка отправлена!</h3>
                <p className="text-gray-600">Мы свяжемся с вами для уточнения деталей</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Имя *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ваше имя"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Телефон *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+7 (___) ___-__-__"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Опишите ваши требования *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Укажите желаемые размеры, тип конструкции, количество дверей/форточек и другие требования..."
                    rows={5}
                    required
                    className="mt-1"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                >
                  Отправить заявку
                </Button>
              </form>
            )}

            {/* Quick Contact */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-center mb-4">Или свяжитесь с нами напрямую:</p>
              <div className="flex flex-wrap justify-center gap-3">
                <a href={`tel:${companyInfo.phoneClean}`}>
                  <Button variant="outline" className="border-emerald-600 text-emerald-700">
                    <Phone className="w-4 h-4 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
                <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-green-500 text-green-600">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <a href={companyInfo.social.telegram} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-sky-500 text-sky-600">
                    <Send className="w-4 h-4 mr-2" />
                    Telegram
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomPage;
